import React from 'react';
import {
  Button as AriaButton,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarStateContext,
  CalendarYearPicker,
} from 'react-aria-components';
import Icon from '../../../../atoms/Icon/Icon';
import { TIME_ZONE } from '../dateUtils';

/**
 * CalendarContent — the shared inner markup of react-aria's `<Calendar>`: month navigation (prev/next + heading)
 * and the day grid. It is meant to be rendered as the **children** of a `<Calendar>`, both in the controlled
 * standalone case (`DatePickerInline`) and inside the popover shell shared by `DatePicker` and `DatePickerActions`.
 *
 * It is purely presentational and context-driven: the nav buttons use `slot`, the cells are generated by
 * react-aria, and the headings read the visible month from `CalendarStateContext` — so it never needs the
 * calendar `value`/`onChange`. With `hasYearPicker`, the heading shows only the month and a native `<select>`
 * (fed by react-aria's `CalendarYearPicker`) lets the user jump years; month is still changed via prev/next.
 * The only other input is `locale`, used to format the month/year heading. Styled with `.month-calendar` tokens.
 */

// Combined "month year" heading (default, when there is no year picker).
function CalendarHeading({ locale }: { locale: string }): JSX.Element {
  const state = React.useContext(CalendarStateContext);
  const start = state?.visibleRange?.start;
  // react-aria's <Heading> uses the locale's long format, which in Spanish reads "junio de 2026"; we format the
  // month and year separately and join with a space → "junio 2026".
  const text = start ? `${new Intl.DateTimeFormat(locale, { month: 'long' }).format(start.toDate(TIME_ZONE))} ${start.year}` : '';
  return <h2 className="month-calendar__heading">{text}</h2>;
}

// Month-only heading, used next to the year <select> when `hasYearPicker` is on.
function CalendarMonthHeading({ locale }: { locale: string }): JSX.Element {
  const state = React.useContext(CalendarStateContext);
  const start = state?.visibleRange?.start;
  const text = start ? new Intl.DateTimeFormat(locale, { month: 'long' }).format(start.toDate(TIME_ZONE)) : '';
  return <span className="month-calendar__heading">{text}</span>;
}

// Native <select> to jump years, driven by react-aria's CalendarYearPicker (it builds the year items, knows the
// focused year and moves the calendar on change). A native select avoids a popover-inside-the-popover.
function YearPicker(): JSX.Element {
  return (
    <CalendarYearPicker>
      {({ 'aria-label': ariaLabel, items, onChange, value }) => (
        <select aria-label={ariaLabel} className="month-calendar__year-select" onChange={(e) => onChange(Number(e.target.value))} value={String(value)}>
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.formatted}
            </option>
          ))}
        </select>
      )}
    </CalendarYearPicker>
  );
}

type CalendarContentProps = {
  hasYearPicker?: boolean;
  locale: string;
};

function CalendarContent({ hasYearPicker = false, locale }: CalendarContentProps): JSX.Element {
  return (
    <>
      {/* Plain div, not <header>: react-aria's <Calendar> root already carries role="application" (deliberate,
      for its own keyboard handling), and a semantic <header> (implicit role="banner") nested inside trips axe's
      landmark-banner-is-top-level rule — landmarks don't apply inside a widget's internal chrome anyway. */}
      <div className="month-calendar__nav">
        <AriaButton className="month-calendar__nav-button" slot="previous">
          <Icon name="chevron-left" size="lg" />
        </AriaButton>
        {hasYearPicker ? (
          <div className="month-calendar__heading-group">
            <CalendarMonthHeading locale={locale} />
            <YearPicker />
          </div>
        ) : (
          <CalendarHeading locale={locale} />
        )}
        <AriaButton className="month-calendar__nav-button" slot="next">
          <Icon name="chevron-right" size="lg" />
        </AriaButton>
      </div>
      <CalendarGrid className="month-calendar__grid">
        <CalendarGridHeader>{(day) => <CalendarHeaderCell className="month-calendar__weekday">{day}</CalendarHeaderCell>}</CalendarGridHeader>
        <CalendarGridBody>{(date) => <CalendarCell className="month-calendar__cell" date={date} />}</CalendarGridBody>
      </CalendarGrid>
    </>
  );
}

export default CalendarContent;
